<template>
  <div class="fw-tabs-anchor" :ref="setNavRef" :style="anchorStyle">
    <div class="fw-tabs-nav" :class="[ { 'fw-tabs-nav--open': mobileOpen, 'fw-tabs-nav--stuck': isStuck } ]" :style="navStyle">
      <!-- Mobile: Dropdown toggle -->
      <button
        type="button"
        class="fw-tabs-toggle"
        @click="toggleMobile"
        :aria-expanded="mobileOpen"
        aria-haspopup="listbox"
      >
        <span class="fw-tabs-toggle__label">
          <span class="fw-tabs-toggle__index">{{ (current$ && current$.index !== undefined ? current$.index : 0) + 1 }}/{{ Object.keys(visible$ || {}).length }}</span>
          <span class="fw-tabs-toggle__text" v-html="current$ && current$.tabLabel ? current$.tabLabel : ''"></span>
        </span>
        <span v-if="invalidTabCount > 0" class="fw-tabs-toggle__error" :aria-label="t('offscreenErrors')">
          <span class="fw-tabs-toggle__error-icon">!</span>
          <span class="fw-tabs-toggle__error-count">{{ invalidTabCount }}</span>
        </span>
        <svg class="fw-tabs-toggle__chevron" :class="{ 'fw-tabs-toggle__chevron--open': mobileOpen }" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <!-- Desktop: scrollable tab bar with arrows -->
      <div class="fw-tabs-scroll-wrapper" style="flex: 1 1 auto; min-width: 0;">
        <!-- Left scroll arrow (triple chevron) -->
        <button
          v-if="canScrollLeft"
          type="button"
          class="fw-tabs-arrow fw-tabs-arrow--left"
          @click="scrollBy(-1)"
          :aria-label="t('scrollLeft')"
        >
          <svg viewBox="0 0 64 66" fill="currentColor">
            <g transform="translate(64,66) scale(-0.1,-0.1)">
              <path d="M137 493c-20-19-3-60 48-120l55-65-56-63c-44-51-55-70-52-92 2-22 9-29 29-31 23-3 38 10 100 83 41 48 74 95 74 105 0 30-142 190-169 190-12 0-26-3-29-7z"/>
              <path d="M265 480c-4-6 22-44 57-86 35-41 63-79 63-84 0-5-28-43-62-84-64-77-74-96-49-96 15 0 166 163 166 180 0 12-150 180-161 180-4 0-11-5-14-10z"/>
              <path d="M355 481c-3-5 24-44 60-86 36-42 65-81 65-87 0-5-29-43-65-84-61-70-72-94-41-94 14 0 156 164 156 180 0 16-143 180-157 180-6 0-14-4-18-9z"/>
            </g>
          </svg>
        </button>

        <!-- Fade indicators -->
        <div v-if="canScrollLeft" class="fw-tabs-fade fw-tabs-fade--left"></div>
        <div v-if="canScrollRight" class="fw-tabs-fade fw-tabs-fade--right"></div>

        <!-- Tab list -->
        <ul :class="classes.container" class="fw-tabs-list" role="tablist" :ref="setListRef" @click="closeMobile">
          <slot>
            <FormTab
              v-for="(tab, name, i) in tabs"
              v-bind="tab"
              :name="name"
              :index="i"
              :key="name"
            />
          </slot>
        </ul>

        <!-- Right scroll arrow (triple chevron) -->
        <button
          v-if="canScrollRight"
          type="button"
          class="fw-tabs-arrow fw-tabs-arrow--right"
          @click="scrollBy(1)"
          :aria-label="t('scrollRight')"
        >
          <svg viewBox="0 0 64 66" fill="currentColor">
            <g transform="translate(0,66) scale(0.1,-0.1)">
              <path d="M137 493c-20-19-3-60 48-120l55-65-56-63c-44-51-55-70-52-92 2-22 9-29 29-31 23-3 38 10 100 83 41 48 74 95 74 105 0 30-142 190-169 190-12 0-26-3-29-7z"/>
              <path d="M265 480c-4-6 22-44 57-86 35-41 63-79 63-84 0-5-28-43-62-84-64-77-74-96-49-96 15 0 166 163 166 180 0 12-150 180-161 180-4 0-11-5-14-10z"/>
              <path d="M355 481c-3-5 24-44 60-86 36-42 65-81 65-87 0-5-29-43-65-84-61-70-72-94-41-94 14 0 156 164 156 180 0 16-143 180-157 180-6 0-14-4-18-9z"/>
            </g>
          </svg>
        </button>

        <!-- Off-screen error indicator -->
        <div v-if="hasOffscreenErrors" class="fw-tabs-error-hint" @click="scrollToFirstError">
          <span class="fw-tabs-error-hint__badge">!</span>
          <span class="fw-tabs-error-hint__text">{{ t('offscreenErrors') }}</span>
        </div>
      </div>

      <!-- Save button (update mode only) -->
      <div v-if="isUpdate" class="fw-tabs-save" style="flex-shrink: 0; display: flex; align-items: stretch;">
        <button
          type="button"
          class="uk-button uk-button-primary fw-tabs-save__btn"
          :disabled="!isDirty || isSubmitting"
          :aria-label="t('save')"
          @click="submitForm"
          style="border: 0; border-radius: 0; margin: 0; padding: 0 1.25rem; height: auto; align-self: stretch; white-space: nowrap; line-height: 1; font-weight: 600;"
        >
          {{ isSubmitting ? t('saving') : (customSaveLabel || t('save')) }}
        </button>
      </div>

      <!-- Backdrop for mobile dropdown -->
      <div v-if="mobileOpen" class="fw-tabs-backdrop" @click="closeMobile"></div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'FormTabs',
    data() {
      return {
        merge: true,
        defaultClasses: {
          container: 'vf-tabs',
          container_sm: 'vf-tabs-sm',
          container_md: '',
          container_lg: 'vf-tabs-lg',
          $container: (classes, { Size }) => ([
            classes.container,
            classes[`container_${Size}`],
          ]),
        }
      }
    }
  }
</script>

<!-- Styles in assets/styles/newform/_tabs.scss -->
